import i18n from '../i18n';
// 320 (owner ruling): the finance form's from/to account dropdowns must EXCLUDE
// AUTO/engine-managed accounts — the ones a generator posts to (COGS, inventory,
// GIT, supplier-credit, the holding accounts, …), never a human.
//
// ── 829: THIS MODULE NO LONGER OWNS A ROLE LIST ───────────────────────────────
// It used to carry its own ENGINE_ROLES Set + the inventory_*/git_* prefix rule + the
// 'Retained Earnings' name carve-out. That made it the SIXTH hand-copy of a role set across these
// two repos (792 counted five already), and — worse — the picker was the ONLY thing that enforced
// it: the exclusion was client-side, so a direct POST /api/finance to an engine account was simply
// accepted. 829 moved BOTH halves to where they belong:
//
//   THE SET       lives ONCE, in the api: ENGINE_MANAGED_ROLES in server.js, composed from the
//                 role sets that already existed there (SYSTEM_EXPENSE_ROLES + HOLDING_ROLES + the
//                 four engine-only roles) plus the inventory_*/git_* PREFIX rule and the roleless
//                 'Retained Earnings' name carve-out.
//   THE VERDICT   arrives per-account as `is_engine_managed`, stamped on every row served by
//                 GET /api/accounts (which is what BOTH api.getAccounts() and
//                 api.getSelectableAccounts() call). That is the repo's established pattern for a
//                 server-owned constant reaching the browser — the same shape as
//                 category_constants → GET /api/category-constants → config/categoryConstants.js.
//   THE GUARD     is server-side now, on POST and PUT /api/finance, keyed on the SAME predicate.
//
// So there is nothing left here to drift, and this filter is a DISPLAY convenience over a rule the
// server enforces independently. It is DERIVED, never stored: no is_engine_managed column exists,
// because five live routes mint future is_system accounts and any backfilled column would go
// stale (827).
//
// DEGRADED BEHAVIOUR, STATED HONESTLY: if a caller ever hands this an account object that did NOT
// come from GET /api/accounts, the flag is absent and the account is treated as NOT engine-managed
// — it shows up in the picker. That fails toward a LOUD refusal (the server returns 422 naming the
// account), never toward a silent bad posting. It is deliberately not "fail closed to an empty
// dropdown", and deliberately not a local fallback list — a fallback list is exactly the sixth copy
// this change removes. OBSERVED at 829: both call sites (DataEntryPage.jsx FinanceForm and
// Forms/LinkedTransactionFields.jsx) are fed by api.getSelectableAccounts(), so the flag is present.

export function isEngineManagedAccount(a) {
  return a?.is_engine_managed === true;
}

export function excludeEngineAccounts(accounts) {
  return (accounts || []).filter(a => !isEngineManagedAccount(a));
}

// A posted manual row whose judged account is engine-managed. Owner rule 2026-10-09 ("classification edit:
// yes, account reassignment: no"): the server (PUT /api/finance/:id) now accepts classification / attribution
// (cost class, scope, band/farm/site, project, tags) and notes on such a row, and still 422s an account change
// or an amount / bucket / paid-from change (AUTO rows stay fully locked). So editors open EDITABLE for
// classification with the badge + this hint; account, amount, bucket, paid-from and date are disabled.
// `field` is the account side judged: account_to_id for an Expense, account_from_id for the Other Revenue
// nature side (UI-stricter than the server, which only judges a CHANGED revenue account).

// 47189 (owner R-CM): the same lock covers the system-generated Expense / Other Revenue legs (bad debts, discounts, order credits, goodwill tenders, the forfeited
// customer credits leg): shown on the re-allocation doors with this marker, allocation editable, money locked. EN + FR (common:shared.systemManaged).
// 47232: the verdict is the SERVER's ONE engine-written predicate (api lib/engineLeg), stamped `system_managed` on every GET /api/finance row and allocation share: it also
// covers an engine leg whose judged account is ordinary (a return: Egg sales -> Customer Refund Held; a fee settlement: AR -> Payables). A row without the stamp (a caller
// not fed by those reads) falls back to the judged account's own flag.
export function systemManagedLock(row, accounts, field = 'account_to_id') {
  const acc = (accounts || []).find(a => a.id === row?.[field]);
  const managed = typeof row?.system_managed === 'boolean' ? row.system_managed : isEngineManagedAccount(acc);
  if (!managed) return null;
  const onAccount = isEngineManagedAccount(acc);
  return { account: acc || null, leg: !onAccount, reason: onAccount ? systemManagedReason(acc) : systemLegReason() };
}

export function systemManagedReason(acc) {
  return i18n.t('common:shared.systemManaged.reason', { name: acc?.name || '', role: acc?.account_role ? ` (${acc.account_role})` : '' });
}
// an engine leg whose judged account is an ordinary one: the posting, not the account, is the system's
export const systemLegReason = () => i18n.t('common:shared.systemManaged.legReason');
